import {Affiliation} from '../../backend/interfaces/affiliation';
import {Application} from '../../backend/interfaces/application';
import {ApplicationCategory} from '../../backend/interfaces/application-category';
import {Device} from '../../backend/interfaces/device';
import {Entity} from '../../backend/interfaces/entity';
import {Playlist} from '../../backend/interfaces/playlist';
import {PlaylistItem} from '../../backend/interfaces/playlist-item';
import {User} from '../../backend/interfaces/user';
import {UserCard} from '../../backend/interfaces/user-card';
import {SubmitButtonState} from '../../components/submit-button/submit-button';
import {CaregiverMode} from '../../enums/caregiver-mode';
import {InviteIdentification} from '../../interfaces/invite-identification';
import {Call} from 'voximplant-websdk/Call/Call';
import {AffiliationQuickAccess} from '../../backend/interfaces/affiliation-quick-access';

export enum VoximplantProcessState {
  Undefined = 0,
  InProgress = 1,
  Done = 2,
  Failed = 3,
}
export interface VoximplantState {
  sdkVersion: string;
  initState: VoximplantProcessState;
  connectionState: VoximplantProcessState;
  signedInState: VoximplantProcessState;
  signedOutState: VoximplantProcessState;
  degraded: boolean;
}

export interface VideocallStateParticipantInfo {
  id: string;
  name: string;
  profilePicture: string;
}

export interface VideocallState {
  callIncoming: boolean;
  callIncomingAction?: string;
  callIncomingPendingRecovery?: boolean;
  open: boolean;
  screenMode: 'full-screen' | 'picture-in-picture';
  callTimer?: string;
  currentCall?: Call;
  callerInfo?: VideocallStateParticipantInfo;
}

export interface IncomingCallState {
  callId: string;
  action: string;
  appAlreadyOpened: boolean;
  notification: any;
}

export interface Occurrence {
  id: string;
  entityId: string;
  date: string;
  appAlreadyOpened: boolean;
}

export interface OfficeContextState {
  pendingTasksVisible: boolean;
  drawerOpened: boolean;
  drawerCollapsed: boolean;
  drawerCollapeDisabled: boolean;
  currentCaregiverModeEnabled: CaregiverMode;
  sideNavigationDrawerCollapsed: boolean;
  userForm?: {
    user: User;
    affiliation?: Affiliation;
    affiliationQuickAccess?: AffiliationQuickAccess;
  };
  userFormLoadingState: boolean;
  userFormSubmitButtonState?: SubmitButtonState;
  userFormDeleteButtonState?: SubmitButtonState;
  entityForm?: Entity;
  entityFormLoadingState: boolean;
  entityFormDeleteButtonState?: SubmitButtonState;
  entityFormSubmitButtonState?: SubmitButtonState;
  userCardForm?: UserCard;
  userCardFormDeleteButtonState?: SubmitButtonState;
  userCardFormSubmitButtonState?: SubmitButtonState;
  deviceForm?: Device;
  deviceFormDeleteButtonState?: SubmitButtonState;
  deviceFormSubmitButtonState?: SubmitButtonState;
  applicationCategoryForm?: ApplicationCategory;
  applicationCategoryFormDeleteButtonState?: SubmitButtonState;
  applicationCategoryFormSubmitButtonState?: SubmitButtonState;
  playlistForm?: Playlist;
  playlistFormDeleteButtonState?: SubmitButtonState;
  playlistFormSubmitButtonState?: SubmitButtonState;
  playlistItemForm?: PlaylistItem;
  playlistItemFormDeleteButtonState?: SubmitButtonState;
  playlistItemFormSubmitButtonState?: SubmitButtonState;
  applicationForm?: Application;
  professionalCaregiverInviteFormSubmitButtonState?: SubmitButtonState;
  informalCaregiverInviteFormSubmitButtonState?: SubmitButtonState;
  associateUserCardFormSubmitButtonState?: SubmitButtonState;
  associateDeviceFormSubmitButtonState?: SubmitButtonState;
  invite?: InviteIdentification;
  occurrence?: Occurrence;
  voximplant: VoximplantState;
  videocall: VideocallState;
  incomingCall?: IncomingCallState;
}

export enum OfficeContextActionType {
  TOGGLE_DRAWER = 'TOGGLE_DRAWER',
  TOGGLE_NAVIGATION_DRAWER = 'TOGGLE_NAVIGATION_DRAWER',
  SET_CURRENT_CAREGIVER_MODE_ENABLED = 'SET_CURRENT_CAREGIVER_MODE_ENABLED',
  SET_USER_FORM = 'SET_USER_FORM',
  SET_USER_FORM_LOAD_STATE = 'SET_USER_FORM_LOAD_STATE',
  CLEAR_USER_FORM = 'CLEAR_USER_FORM',
  SET_USER_FORM_SUBMIT_BUTTON_STATE = 'SET_USER_FORM_SUBMIT_BUTTON_STATE',
  CLEAR_USER_FORM_SUBMIT_BUTTON_STATE = 'CLEAR_USER_FORM_SUBMIT_BUTTON_STATE',
  SET_USER_FORM_DELETE_BUTTON_STATE = 'SET_USER_FORM_DELETE_BUTTON_STATE',
  CLEAR_USER_FORM_DELETE_BUTTON_STATE = 'CLEAR_USER_FORM_DELETE_BUTTON_STATE',
  SET_INFORMAL_CAREGIVER_INVITE_FORM_SUBMIT_BUTTON_STATE = 'SET_INFORMAL_CAREGIVER_INVITE_FORM_SUBMIT_BUTTON_STATE',
  CLEAR_INFORMAL_CAREGIVER_INVITE_FORM_SUBMIT_BUTTON_STATE = 'CLEAR_INFORMAL_CAREGIVER_INVITE_FORM_SUBMIT_BUTTON_STATE',
  SET_APPLICATION_FORM = 'SET_APPLICATION_FORM',
  CLEAR_APPLICATION_FORM = 'CLEAR_APPLICATION_FORM',
  SET_PLAYLIST_FORM = 'SET_PLAYLIST_FORM',
  CLEAR_PLAYLIST_FORM = 'CLEAR_PLAYLIST_FORM',
  SET_PLAYLIST_FORM_SUBMIT_BUTTON_STATE = 'SET_PLAYLIST_FORM_SUBMIT_BUTTON_STATE',
  CLEAR_PLAYLIST_FORM_SUBMIT_BUTTON_STATE = 'CLEAR_PLAYLIST_FORM_SUBMIT_BUTTON_STATE',
  SET_PLAYLIST_FORM_DELETE_BUTTON_STATE = 'SET_PLAYLIST_FORM_DELETE_BUTTON_STATE',
  CLEAR_PLAYLIST_FORM_DELETE_BUTTON_STATE = 'CLEAR_PLAYLIST_FORM_DELETE_BUTTON_STATE',
  SET_INVITE = 'SET_INVITE',
  SET_OCCURRENCE = 'SET_OCCURRENCE',
  CLEAR_INVITE = 'CLEAR_INVITE',
  CLEAR_OCCURRENCE = 'CLEAR_OCCURRENCE',
  SET_ENTITY_FORM = 'SET_ENTITY_FORM',
  SET_ENTITY_FORM_LOAD_STATE = 'SET_ENTITY_FORM_LOAD_STATE',
  CLEAR_ENTITY_FORM = 'CLEAR_ENTITY_FORM',
  SET_ENTITY_FORM_SUBMIT_BUTTON_STATE = 'SET_ENTITY_FORM_SUBMIT_BUTTON_STATE',
  CLEAR_ENTITY_FORM_SUBMIT_BUTTON_STATE = 'CLEAR_ENTITY_FORM_SUBMIT_BUTTON_STATE',
  SET_ENTITY_FORM_DELETE_BUTTON_STATE = 'SET_ENTITY_FORM_DELETE_BUTTON_STATE',
  CLEAR_ENTITY_FORM_DELETE_BUTTON_STATE = 'CLEAR_ENTITY_FORM_DELETE_BUTTON_STATE',
  SET_APPLICATION_CATEGORY_FORM = 'SET_APPLICATION_CATEGORY_FORM',
  CLEAR_APPLICATION_CATEGORY_FORM = 'CLEAR_APPLICATION_CATEGORY_FORM',
  SET_APPLICATION_CATEGORY_FORM_SUBMIT_BUTTON_STATE = 'SET_APPLICATION_CATEGORY_FORM_SUBMIT_BUTTON_STATE',
  CLEAR_APPLICATION_CATEGORY_FORM_SUBMIT_BUTTON_STATE = 'CLEAR_APPLICATION_CATEGORY_FORM_SUBMIT_BUTTON_STATE',
  SET_APPLICATION_CATEGORY_FORM_DELETE_BUTTON_STATE = 'SET_APPLICATION_CATEGORY_FORM_DELETE_BUTTON_STATE',
  CLEAR_APPLICATION_CATEGORY_FORM_DELETE_BUTTON_STATE = 'CLEAR_APPLICATION_CATEGORY_FORM_DELETE_BUTTON_STATE',
  SET_PROFESSIONAL_CAREGIVER_INVITE_FORM_SUBMIT_BUTTON_STATE = 'SET_PROFESSIONAL_CAREGIVER_INVITE_FORM_SUBMIT_BUTTON_STATE',
  CLEAR_PROFESSIONAL_CAREGIVER_INVITE_FORM_SUBMIT_BUTTON_STATE = 'CLEAR_PROFESSIONAL_CAREGIVER_INVITE_FORM_SUBMIT_BUTTON_STATE',
  SET_ASSOCIATE_USER_CARD_FORM_SUBMIT_BUTTON_STATE = 'SET_ASSOCIATE_USER_CARD_FORM_SUBMIT_BUTTON_STATE',
  CLEAR_ASSOCIATE_USER_CARD_FORM_SUBMIT_BUTTON_STATE = 'CLEAR_ASSOCIATE_USER_FORM_CARD_SUBMIT_BUTTON_STATE',
  SET_ASSOCIATE_DEVICE_FORM_SUBMIT_BUTTON_STATE = 'SET_ASSOCIATE_DEVICE_FORM_SUBMIT_BUTTON_STATE',
  CLEAR_ASSOCIATE_DEVICE_FORM_SUBMIT_BUTTON_STATE = 'CLEAR_ASSOCIATE_DEVICE_FORM_SUBMIT_BUTTON_STATE',
  SET_USER_CARD_FORM = 'SET_USER_CARD_FORM',
  CLEAR_USER_CARD_FORM = 'CLEAR_USER_CARD_FORM',
  SET_USER_CARD_FORM_SUBMIT_BUTTON_STATE = 'SET_USER_CARD_FORM_SUBMIT_BUTTON_STATE',
  CLEAR_USER_CARD_FORM_SUBMIT_BUTTON_STATE = 'CLEAR_USER_CARD_FORM_SUBMIT_BUTTON_STATE',
  SET_USER_CARD_FORM_DELETE_BUTTON_STATE = 'SET_USER_CARD_FORM_DELETE_BUTTON_STATE',
  CLEAR_USER_CARD_FORM_DELETE_BUTTON_STATE = 'CLEAR_USER_CARD_FORM_DELETE_BUTTON_STATE',
  SET_DEVICE_FORM = 'SET_DEVICE_FORM',
  CLEAR_DEVICE_FORM = 'CLEAR_DEVICE_FORM',
  SET_DEVICE_FORM_SUBMIT_BUTTON_STATE = 'SET_DEVICE_FORM_SUBMIT_BUTTON_STATE',
  CLEAR_DEVICE_FORM_SUBMIT_BUTTON_STATE = 'CLEAR_DEVICE_FORM_SUBMIT_BUTTON_STATE',
  SET_DEVICE_FORM_DELETE_BUTTON_STATE = 'SET_DEVICE_FORM_DELETE_BUTTON_STATE',
  CLEAR_DEVICE_FORM_DELETE_BUTTON_STATE = 'CLEAR_DEVICE_FORM_DELETE_BUTTON_STATE',
  SET_PLAYLIST_ITEM_FORM = 'SET_PLAYLIST_ITEM_FORM',
  CLEAR_PLAYLIST_ITEM_FORM = 'CLEAR_PLAYLIST_ITEM_FORM',
  SET_PLAYLIST_ITEM_FORM_SUBMIT_BUTTON_STATE = 'SET_PLAYLIST_ITEM_FORM_SUBMIT_BUTTON_STATE',
  CLEAR_PLAYLIST_ITEM_FORM_SUBMIT_BUTTON_STATE = 'CLEAR_PLAYLIST_ITEM_FORM_SUBMIT_BUTTON_STATE',
  SET_PLAYLIST_ITEM_FORM_DELETE_BUTTON_STATE = 'SET_PLAYLIST_ITEM_FORM_DELETE_BUTTON_STATE',
  CLEAR_PLAYLIST_ITEM_FORM_DELETE_BUTTON_STATE = 'CLEAR_PLAYLIST_ITEM_FORM_DELETE_BUTTON_STATE',
  UPDATE_VOXIMPLANT_STATE = 'UPDATE_VOXIMPLANT_STATE',
  UPDATE_VIDEOCALL_STATE = 'UPDATE_VIDEOCALL_STATE',
  SET_INCOMING_CALL = 'SET_INCOMING_CALL',
  COLLAPSE_DRAWER = 'COLLAPSE_DRAWER',
  HIDE_PENDING_TASKS = 'HIDE_PENDING_TASKS',
}

export type OfficeContextAction =
  | {type: OfficeContextActionType.HIDE_PENDING_TASKS; pendingTasksVisible: boolean}
  | {
      type: OfficeContextActionType.TOGGLE_DRAWER;
      drawerOpened: boolean;
    }
  | {
      type: OfficeContextActionType.TOGGLE_NAVIGATION_DRAWER;
      sideNavigationDrawerCollapsed: boolean;
    }
  | {
      type: OfficeContextActionType.SET_CURRENT_CAREGIVER_MODE_ENABLED;
      mode: CaregiverMode;
    }
  | {
      type: OfficeContextActionType.SET_USER_FORM;
      user: User;
      affiliation?: Affiliation;
      affiliationQuickAccess?: AffiliationQuickAccess;
    }
  | {
      type: OfficeContextActionType.SET_USER_FORM_LOAD_STATE;
      state: boolean;
    }
  | {
      type: OfficeContextActionType.CLEAR_USER_FORM;
    }
  | {
      type: OfficeContextActionType.SET_USER_FORM_SUBMIT_BUTTON_STATE;
      state: SubmitButtonState;
    }
  | {
      type: OfficeContextActionType.CLEAR_USER_FORM_SUBMIT_BUTTON_STATE;
    }
  | {
      type: OfficeContextActionType.SET_USER_FORM_DELETE_BUTTON_STATE;
      state: SubmitButtonState;
    }
  | {
      type: OfficeContextActionType.CLEAR_USER_FORM_DELETE_BUTTON_STATE;
    }
  | {
      type: OfficeContextActionType.SET_INFORMAL_CAREGIVER_INVITE_FORM_SUBMIT_BUTTON_STATE;
      state: SubmitButtonState;
    }
  | {
      type: OfficeContextActionType.CLEAR_INFORMAL_CAREGIVER_INVITE_FORM_SUBMIT_BUTTON_STATE;
    }
  | {
      type: OfficeContextActionType.SET_APPLICATION_FORM;
      application: Application;
    }
  | {
      type: OfficeContextActionType.CLEAR_APPLICATION_FORM;
    }
  | {
      type: OfficeContextActionType.SET_INVITE;
      invite: InviteIdentification;
    }
  | {
      type: OfficeContextActionType.SET_OCCURRENCE;
      occurrence: Occurrence;
    }
  | {
      type: OfficeContextActionType.CLEAR_INVITE;
    }
  | {
      type: OfficeContextActionType.CLEAR_OCCURRENCE;
    }
  | {
      type: OfficeContextActionType.SET_ENTITY_FORM;
      entity: Entity;
    }
  | {
      type: OfficeContextActionType.SET_ENTITY_FORM_LOAD_STATE;
      state: boolean;
    }
  | {
      type: OfficeContextActionType.CLEAR_ENTITY_FORM;
    }
  | {
      type: OfficeContextActionType.SET_ENTITY_FORM_SUBMIT_BUTTON_STATE;
      state: SubmitButtonState;
    }
  | {
      type: OfficeContextActionType.CLEAR_ENTITY_FORM_SUBMIT_BUTTON_STATE;
    }
  | {
      type: OfficeContextActionType.SET_ENTITY_FORM_DELETE_BUTTON_STATE;
      state: SubmitButtonState;
    }
  | {
      type: OfficeContextActionType.CLEAR_ENTITY_FORM_DELETE_BUTTON_STATE;
    }
  | {
      type: OfficeContextActionType.SET_APPLICATION_CATEGORY_FORM;
      applicationCategory: ApplicationCategory;
    }
  | {
      type: OfficeContextActionType.CLEAR_APPLICATION_CATEGORY_FORM;
    }
  | {
      type: OfficeContextActionType.SET_APPLICATION_CATEGORY_FORM_SUBMIT_BUTTON_STATE;
      state: SubmitButtonState;
    }
  | {
      type: OfficeContextActionType.CLEAR_APPLICATION_CATEGORY_FORM_SUBMIT_BUTTON_STATE;
    }
  | {
      type: OfficeContextActionType.SET_APPLICATION_CATEGORY_FORM_DELETE_BUTTON_STATE;
      state: SubmitButtonState;
    }
  | {
      type: OfficeContextActionType.CLEAR_APPLICATION_CATEGORY_FORM_DELETE_BUTTON_STATE;
    }
  | {
      type: OfficeContextActionType.SET_PROFESSIONAL_CAREGIVER_INVITE_FORM_SUBMIT_BUTTON_STATE;
      state: SubmitButtonState;
    }
  | {
      type: OfficeContextActionType.CLEAR_PROFESSIONAL_CAREGIVER_INVITE_FORM_SUBMIT_BUTTON_STATE;
    }
  | {
      type: OfficeContextActionType.SET_ASSOCIATE_USER_CARD_FORM_SUBMIT_BUTTON_STATE;
      state: SubmitButtonState;
    }
  | {
      type: OfficeContextActionType.CLEAR_ASSOCIATE_USER_CARD_FORM_SUBMIT_BUTTON_STATE;
    }
  | {
      type: OfficeContextActionType.SET_ASSOCIATE_DEVICE_FORM_SUBMIT_BUTTON_STATE;
      state: SubmitButtonState;
    }
  | {
      type: OfficeContextActionType.CLEAR_ASSOCIATE_DEVICE_FORM_SUBMIT_BUTTON_STATE;
    }
  | {
      type: OfficeContextActionType.SET_USER_CARD_FORM;
      userCard: UserCard;
    }
  | {
      type: OfficeContextActionType.CLEAR_USER_CARD_FORM;
    }
  | {
      type: OfficeContextActionType.SET_USER_CARD_FORM_SUBMIT_BUTTON_STATE;
      state: SubmitButtonState;
    }
  | {
      type: OfficeContextActionType.CLEAR_USER_CARD_FORM_SUBMIT_BUTTON_STATE;
    }
  | {
      type: OfficeContextActionType.SET_USER_CARD_FORM_DELETE_BUTTON_STATE;
      state: SubmitButtonState;
    }
  | {
      type: OfficeContextActionType.CLEAR_USER_CARD_FORM_DELETE_BUTTON_STATE;
    }
  | {
      type: OfficeContextActionType.SET_DEVICE_FORM;
      device: Device;
    }
  | {
      type: OfficeContextActionType.CLEAR_DEVICE_FORM;
    }
  | {
      type: OfficeContextActionType.SET_DEVICE_FORM_SUBMIT_BUTTON_STATE;
      state: SubmitButtonState;
    }
  | {
      type: OfficeContextActionType.CLEAR_DEVICE_FORM_SUBMIT_BUTTON_STATE;
    }
  | {
      type: OfficeContextActionType.SET_DEVICE_FORM_DELETE_BUTTON_STATE;
      state: SubmitButtonState;
    }
  | {
      type: OfficeContextActionType.CLEAR_DEVICE_FORM_DELETE_BUTTON_STATE;
    }
  | {
      type: OfficeContextActionType.SET_PLAYLIST_FORM;
      playlist: Playlist;
    }
  | {
      type: OfficeContextActionType.CLEAR_PLAYLIST_FORM;
    }
  | {
      type: OfficeContextActionType.SET_PLAYLIST_FORM_SUBMIT_BUTTON_STATE;
      state: SubmitButtonState;
    }
  | {
      type: OfficeContextActionType.CLEAR_PLAYLIST_FORM_SUBMIT_BUTTON_STATE;
    }
  | {
      type: OfficeContextActionType.SET_PLAYLIST_FORM_DELETE_BUTTON_STATE;
      state: SubmitButtonState;
    }
  | {
      type: OfficeContextActionType.CLEAR_PLAYLIST_FORM_DELETE_BUTTON_STATE;
    }
  | {
      type: OfficeContextActionType.SET_PLAYLIST_ITEM_FORM;
      playlistItem: PlaylistItem;
    }
  | {
      type: OfficeContextActionType.CLEAR_PLAYLIST_ITEM_FORM;
    }
  | {
      type: OfficeContextActionType.SET_PLAYLIST_ITEM_FORM_SUBMIT_BUTTON_STATE;
      state: SubmitButtonState;
    }
  | {
      type: OfficeContextActionType.CLEAR_PLAYLIST_ITEM_FORM_SUBMIT_BUTTON_STATE;
    }
  | {
      type: OfficeContextActionType.SET_PLAYLIST_ITEM_FORM_DELETE_BUTTON_STATE;
      state: SubmitButtonState;
    }
  | {
      type: OfficeContextActionType.CLEAR_PLAYLIST_ITEM_FORM_DELETE_BUTTON_STATE;
    }
  | {
      type: OfficeContextActionType.UPDATE_VOXIMPLANT_STATE;
      state: VoximplantState;
    }
  | {
      type: OfficeContextActionType.UPDATE_VIDEOCALL_STATE;
      state: VideocallState;
    }
  | {
      type: OfficeContextActionType.SET_INCOMING_CALL;
      state: IncomingCallState | undefined;
    }
  | {
      type: OfficeContextActionType.COLLAPSE_DRAWER;
      drawerCollapsed: boolean;
      drawerCollapeDisabled: boolean;
    };

export interface OfficeContextType {
  state: OfficeContextState;

  // Methods
  showPendingTasks: (pendingTasksVisible: boolean) => void;
  toggleDrawer: (drawerOpened: boolean) => void;
  toggleSideNavigationMenuDrawer: (sideNavigationDrawerCollapsed: boolean) => void;
  setCurrentCaregiverModeEnabled: (mode: CaregiverMode) => void;
  setUserForm: (
    user: User,
    affiliation?: Affiliation,
    affiliationQuickAccess?: AffiliationQuickAccess
  ) => void;
  setUserFormLoadState: (state: boolean) => void;
  clearUserForm: () => void;
  setUserFormSubmitButtonState: (state: SubmitButtonState) => void;
  clearUserFormSubmitButtonState: () => void;
  setUserFormDeleteButtonState: (state: SubmitButtonState) => void;
  clearUserFormDeleteButtonState: () => void;
  setInformalCaregiverInviteFormSubmitButtonState: (state: SubmitButtonState) => void;
  clearInformalCaregiverInviteFormSubmitButtonState: () => void;
  setApplicationForm: (application: Application) => void;
  clearApplicationForm: () => void;
  setInvite: (invite: InviteIdentification) => void;
  clearInvite: () => void;
  setOccurrence: (occurrence: Occurrence) => void;
  clearOccurrence: () => void;
  setEntityForm: (entity: Entity) => void;
  setEntityFormLoadState: (state: boolean) => void;
  clearEntityForm: () => void;
  setEntityFormSubmitButtonState: (state: SubmitButtonState) => void;
  clearEntityFormSubmitButtonState: () => void;
  setEntityFormDeleteButtonState: (state: SubmitButtonState) => void;
  clearEntityFormDeleteButtonState: () => void;
  setApplicationCategoryForm: (applicationCategory: ApplicationCategory) => void;
  clearApplicationCategoryForm: () => void;
  setApplicationCategoryFormSubmitButtonState: (state: SubmitButtonState) => void;
  clearApplicationCategoryFormSubmitButtonState: () => void;
  setApplicationCategoryFormDeleteButtonState: (state: SubmitButtonState) => void;
  clearApplicationCategoryFormDeleteButtonState: () => void;
  setProfessionalCaregiverInviteFormSubmitButtonState: (state: SubmitButtonState) => void;
  clearProfessionalCaregiverInviteFormSubmitButtonState: () => void;
  setAssociateUserCardFormSubmitButtonState: (state: SubmitButtonState) => void;
  clearAssociateUserCardFormSubmitButtonState: () => void;
  setAssociateDeviceFormSubmitButtonState: (state: SubmitButtonState) => void;
  clearAssociateDeviceFormSubmitButtonState: () => void;
  setUserCardForm: (userCard: UserCard) => void;
  clearUserCardForm: () => void;
  setUserCardFormSubmitButtonState: (state: SubmitButtonState) => void;
  clearUserCardFormSubmitButtonState: () => void;
  setUserCardFormDeleteButtonState: (state: SubmitButtonState) => void;
  clearUserCardFormDeleteButtonState: () => void;
  setDeviceForm: (device: Device) => void;
  clearDeviceForm: () => void;
  setDeviceFormSubmitButtonState: (state: SubmitButtonState) => void;
  clearDeviceFormSubmitButtonState: () => void;
  setDeviceFormDeleteButtonState: (state: SubmitButtonState) => void;
  clearDeviceFormDeleteButtonState: () => void;
  setPlaylistForm: (playlist: Playlist) => void;
  clearPlaylistForm: () => void;
  setPlaylistFormSubmitButtonState: (state: SubmitButtonState) => void;
  clearPlaylistFormSubmitButtonState: () => void;
  setPlaylistFormDeleteButtonState: (state: SubmitButtonState) => void;
  clearPlaylistFormDeleteButtonState: () => void;
  setPlaylistItemForm: (playlistItem: PlaylistItem) => void;
  clearPlaylistItemForm: () => void;
  setPlaylistItemFormSubmitButtonState: (state: SubmitButtonState) => void;
  clearPlaylistItemFormSubmitButtonState: () => void;
  setPlaylistItemFormDeleteButtonState: (state: SubmitButtonState) => void;
  clearPlaylistItemFormDeleteButtonState: () => void;
  updateVoximplantState: (state: VoximplantState) => void;
  updateVideocallState: (state: VideocallState) => void;
  setIncomingCall: (state: IncomingCallState | undefined) => void;
  collapseDrawer: (drawerCollapsed: boolean, drawerCollapeDisabled: boolean) => void;
}
